import { z } from 'zod';
/**
 * Operator-facing customer search + manual identity bind
 * (`GET /api/conversations/:id/identity/search`,
 * `POST /api/conversations/:id/identity/attach`).
 *
 * Exists because the automatic card-digit resolution binds only when billing
 * counted EXACTLY ONE candidate: on prod one `last4` covers 3.08 Stripe
 * customers on average, so the common case is «5 совпадений — решает человек»
 * and until now the operator had nothing to decide WITH. These routes are that
 * decision surface: read the candidates, pick one, bind it under audit.
 *
 * The wire DTOs mirror `@wit-support/payment`'s domain types field-for-field,
 * with two deliberate differences:
 *  - optional domain fields become `.nullable().default(null)` — the SPA and the
 *    API are deployed separately, so a reader must never crash on a field the
 *    other side has not started sending yet (same rule as `ConversationDTO`);
 *  - `isSelfAccount` is added here, computed server-side (see the route).
 */
/**
 * Query string of `GET /api/conversations/:id/identity/search`.
 *
 * Every field is optional here and NOT «at least one required»: a fully empty
 * query is answered `{ status:'unavailable', reason:'empty_query' }` by the port
 * without a network call, which is a state the panel renders anyway — turning it
 * into a 400 would give the same situation two different shapes.
 *
 * Empty strings are pre-stripped by the route (a blank form field arrives as
 * `?email=`), so `.min(1)` here only ever rejects whitespace.
 */
export const IdentitySearchQuery = z.object({
    /** Exactly four digits — the shape the customer prints, validated before it leaves. */
    last4: z
        .string()
        .trim()
        .regex(/^\d{4}$/, 'last4 must be exactly 4 digits')
        .optional(),
    email: z.string().trim().email().optional(),
    /**
     * 2..200 — exactly what upstream accepts. A single character is answered
     * `400 INVALID_NAME` there, and the route turns any unexpected upstream 400
     * into a 502 the operator reads as «поиск не ответил» — so a `display_name`
     * of one letter would look like an outage. Rejected here instead, where the
     * panel can say which field is wrong.
     */
    name: z.string().trim().min(2).max(200).optional(),
});
/** Why a candidate is in the list. Mirrors `CustomerMatchSignal` verbatim. */
export const CustomerMatchSignalDTO = z.enum([
    'card_last4',
    'wallet_last4',
    'email',
    'billing_email',
    'name',
]);
/**
 * Which service told us about this person.
 *
 * `billing` — the Stripe mirror (`PaymentPort.searchCustomers`): there are
 * charges, so there are cards and counts to show. `users` — the WIT user
 * service (`IdentityResolverPort.resolveByEmail`): the account exists, and this
 * source knows NOTHING about charges — which is why such a candidate carries
 * `null`s rather than zeros. Both at once means one `witUserId` came back from
 * both, and it is ONE row, not two.
 *
 * The search used to ask billing only, so a registered customer who never paid
 * (or whose charge is not mirrored) read as «совпадений нет» — while the
 * AUTOMATIC resolver, which asks the user service, found them.
 */
export const CandidateSourceDTO = z.enum(['billing', 'users']);
/** One card behind a candidate, freshest first. Display data only. */
export const CustomerCardDTO = z.object({
    brand: z.string().nullable().default(null),
    /** Four digits of the REAL card (what is printed on the plastic). */
    last4: z.string().nullable().default(null),
    /** `apple_pay` / `google_pay` / `link`, or null for a plain card charge. */
    walletType: z.string().nullable().default(null),
    /** Four digits of the wallet's Device Account Number. Usually != `last4`. */
    walletLast4: z.string().nullable().default(null),
    chargesCount: z.number().nullable().default(null),
    lastUsedAt: z.string().nullable().default(null),
});
/**
 * One possible customer behind the queried attributes — a CANDIDATE, never a
 * match. `witUserId` is null when the Stripe customer never became a WIT LAB
 * user: the row is still worth showing (charges exist), it just cannot be bound.
 */
export const CustomerCandidateDTO = z.object({
    witUserId: z.string().nullable().default(null),
    stripeCustomerId: z.string().nullable().default(null),
    /** Account e-mail as known upstream. */
    email: z.string().nullable().default(null),
    /** E-mail captured on the charge (`billing_details.email`). */
    billingEmail: z.string().nullable().default(null),
    /** Name captured on the charge (`billing_details.name`). */
    displayName: z.string().nullable().default(null),
    cardBrand: z.string().nullable().default(null),
    cardLast4: z.string().nullable().default(null),
    succeededChargesCount: z.number().nullable().default(null),
    lastChargeAt: z.string().nullable().default(null),
    matchedBy: z.array(CustomerMatchSignalDTO).default([]),
    /**
     * How close the queried name came to the candidate's best known name, 0..1;
     * null when the query carried no name. DISPLAY ONLY — upstream states `1`
     * means «every word of the shorter name matched», NOT «the same person», and
     * the list is not ordered by it.
     */
    nameScore: z.number().nullable().default(null),
    cards: z.array(CustomerCardDTO).default([]),
    /**
     * This candidate is one of OUR OWN mailboxes (a channel account address).
     * Computed by the route from `loadSelfEmailAddresses`, the same source the
     * automatic resolver's self-guard reads — binding it would point every
     * downstream cancel / refund node at our own account.
     */
    isSelfAccount: z.boolean().default(false),
    /**
     * Which service produced this candidate — see {@link CandidateSourceDTO}.
     * Defaults to `billing` so an answer from an admin-api that predates the
     * user-service pass keeps reading as what it is.
     */
    sources: z.array(CandidateSourceDTO).default(['billing']),
    /**
     * Billing knows NO name for this candidate (`billing_details.name` empty on
     * the matched charges and on the customer). Load-bearing when the query
     * carried a name: upstream keeps such a candidate in the answer precisely
     * because «no name» is not «a different name», but the name was then not
     * verified for them — and the operator has to see which of the two it is.
     */
    nameUnknown: z.boolean().default(false),
});
/**
 * Why a search could not be performed at all. The first three are
 * `CustomerSearchUnavailableReason` verbatim; `not_configured` is added for the
 * deployment where `PAYMENT_API_BASE_URL` is unset, so the UI has one exhaustive
 * switch over a single field instead of a `configured` flag beside a status.
 *
 * `search_failed` is the odd one out and deliberately so: the other four
 * describe a STEADY state the operator cannot change by retrying, this one
 * means the call itself broke and «попробуйте ещё раз» is the right advice.
 */
export const IdentitySearchUnavailableReason = z.enum([
    'endpoint_not_deployed',
    'empty_query',
    'name_only_unsupported',
    'not_configured',
    'search_failed',
]);
/**
 * Response for `GET /api/conversations/:id/identity/search`.
 *
 * DISCRIMINATED ON PURPOSE, exactly like `CustomerSearchResult` upstream of it:
 * «the search route is not deployed» and «we searched and nobody matches» are
 * opposite facts for an operator, and collapsing them into an empty list would
 * quietly report «не клиент» during any outage.
 */
export const IdentitySearchResponse = z.discriminatedUnion('status', [
    z.object({
        status: z.literal('ok'),
        candidates: z.array(CustomerCandidateDTO),
        /** Upstream's total match count; may exceed `candidates.length` under the limit. */
        total: z.number(),
        /** `items.length` as UPSTREAM counted it, before per-candidate tolerant drops. */
        returnedCount: z.number(),
        /** An upstream safeguard fired and THE ANSWER IS INCOMPLETE. */
        truncated: z.boolean(),
        /** Candidates come from upstream's looser second name pass (shared surname). */
        nameMatchRelaxed: z.boolean(),
        /**
         * Criteria the SERVER dropped by itself to stop returning an empty answer.
         * Today only `name`.
         *
         * Upstream applies `name` as an AND filter, and on prod `billing_details.name`
         * is set on 63.2% of charges (4.9% of customers) — so a candidate whose name
         * billing simply does not know is filtered out by ANY name, however correct.
         * A search that came back empty with a name is therefore «имя не совпало»,
         * not «клиента нет», and the two lead an operator to opposite actions. When
         * this array is non-empty the panel MUST say the name was not checked.
         */
        droppedCriteria: z.array(z.enum(['name'])).default([]),
        /**
         * How many candidates the same query yields WITHOUT the name. `null` when we
         * did not find out (no name in the query, or upstream has not shipped its own
         * counter and no retry ran).
         */
        totalWithoutName: z.number().nullable().default(null),
        /**
         * The answer IS real but INCOMPLETE, because one of the billing calls behind
         * it did not go through. Two cases produce it: the search failed while the
         * user service still named somebody, and the name-less retry failed after the
         * first attempt came back empty.
         *
         * Not collapsed into `unavailable`: there the operator has nothing at all,
         * here they have either a candidate to bind or a result they should re-run.
         */
        billingUnavailableReason: IdentitySearchUnavailableReason.nullable().default(null),
    }),
    z.object({
        status: z.literal('unavailable'),
        reason: IdentitySearchUnavailableReason,
    }),
]);
/**
 * Prefill for the search form: `GET /api/conversations/:id/identity/search-hints`.
 *
 * `last4` comes from the newest pipeline run's `context.card_last4` — the digits
 * the customer already typed and the extractor already validated, so the
 * operator does not retype them off the message. `name` is the contact's
 * `display_name`. E-mail is deliberately NOT prefilled: an address we already
 * know is the one the automatic exact-match resolver has already tried.
 */
export const IdentitySearchHintsResponse = z.object({
    last4: z.string().nullable().default(null),
    name: z.string().nullable().default(null),
});
/** Body of `POST /api/conversations/:id/identity/attach`. */
export const IdentityAttachRequest = z.object({
    witUserId: z.string().min(1).max(128),
});
/**
 * Outcome of the manual bind.
 *  - `resolved` — bound (or already bound to this very user: the write is
 *    idempotent and non-clobbering);
 *  - `conflict` — the contact is already bound to a DIFFERENT user; the current
 *    value is returned so the UI can say which one. HTTP 409;
 *  - `no_contact` — the conversation has no contact row. HTTP 404.
 */
export const IdentityAttachResponse = z.object({
    status: z.enum(['resolved', 'conflict', 'no_contact']),
    witUserId: z.string().nullable().default(null),
});
//# sourceMappingURL=identity-search.js.map